import React, { useEffect, useState } from 'react';
import './DataTable.css';

interface DataTableColumn {
  key: string;
  label: string;
  type: 'text' | 'number' | 'year';
  editable?: boolean;
  format?: (value: any) => string;
  validate?: (value: any) => string | null; // Return error message or null
}

interface DataTableProps {
  data: any[];
  columns: DataTableColumn[];
  onDataChange?: (
    updatedData: any[],
    meta?: { year?: number; key?: string; action?: 'edit' | 'add' | 'delete' }
  ) => void;
  onRestoreDefaults?: () => void;
  title?: string;
  subtitle?: string;
  maxHeight?: string;
  pageSize?: number;
  highlightedRows?: Set<any>; // Values to highlight (e.g., modified years)
  highlightKey?: string; // Key to match against (e.g., 'year')
  secondaryHighlightedRows?: Set<any>; // Secondary highlight (e.g., ratio-adjusted years)
}

export const DataTable: React.FC<DataTableProps> = ({
  data,
  columns,
  onDataChange,
  onRestoreDefaults,
  title,
  subtitle,
  maxHeight = '400px',
  pageSize = 20,
  highlightedRows,
  highlightKey,
  secondaryHighlightedRows
}) => {
  const [editedData, setEditedData] = useState<any[]>([]);
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; colKey: string } | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    setEditedData([...data]);
  }, [data]);

  const filteredData = editedData.filter(row => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase().trim();
    
    return Object.values(row).some(value => {
      const stringValue = String(value).toLowerCase();
      return stringValue.includes(searchLower);
    });
  });

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const startIndex = currentPage * pageSize;
  const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

  const handleCellEdit = (rowIndex: number, colKey: string, value: any) => {
    const actualRowIndex = startIndex + rowIndex;
    const column = columns.find(col => col.key === colKey);
    
    // Validate the value
    if (column?.validate) {
      const error = column.validate(value);
      const errorKey = `${actualRowIndex}-${colKey}`;
      
      setErrors(prev => {
        const newErrors = { ...prev };
        if (error) {
          newErrors[errorKey] = error;
        } else {
          delete newErrors[errorKey];
        }
        return newErrors;
      });
      
      if (error) return; // Don't update if validation fails
    }

    // Convert value based on column type
    let convertedValue = value;
    if (column?.type === 'number') {
      convertedValue = value === '' ? 0 : Math.round(parseFloat(value)) || 0;
    } else if (column?.type === 'year') {
      convertedValue = value === '' ? new Date().getFullYear() : parseInt(value) || new Date().getFullYear();
    }

    const newData = [...editedData];
    newData[actualRowIndex] = {
      ...newData[actualRowIndex],
      [colKey]: convertedValue
    };
    
    setEditedData(newData);
    
    if (onDataChange) {
      onDataChange(newData, {
        year: Number(newData[actualRowIndex]?.year),
        key: colKey,
        action: 'edit',
      });
    }
  };

  const handleCellClick = (rowIndex: number, colKey: string) => {
    const column = columns.find(col => col.key === colKey);
    if (column?.editable !== false) {
      setEditingCell({ rowIndex, colKey });
    }
  };

  const handleCellBlur = () => {
    setEditingCell(null);
  };

  const formatCellValue = (value: any, column: DataTableColumn) => {
    if (column.format) {
      return column.format(value);
    }
    
    if (column.type === 'number' && typeof value === 'number') {
      return Math.round(value).toLocaleString(undefined, {
        maximumFractionDigits: 0,
      });
    }
    
    return String(value || '');
  };

  const addNewRow = () => {
    const newRow: any = {};
    columns.forEach(col => {
      if (col.type === 'number') {
        newRow[col.key] = 0;
      } else if (col.type === 'year') {
        newRow[col.key] = new Date().getFullYear();
      } else {
        newRow[col.key] = '';
      }
    });
    
    const newData = [...editedData, newRow];
    setEditedData(newData);
    
    if (onDataChange) {
      onDataChange(newData, {
        year: Number(newRow.year),
        action: 'add',
      });
    }
  };

  const deleteRow = (rowIndex: number) => {
    const actualRowIndex = startIndex + rowIndex;
    const removedYear = Number(editedData[actualRowIndex]?.year);
    const newData = editedData.filter((_, index) => index !== actualRowIndex);
    setEditedData(newData);
    
    if (onDataChange) {
      onDataChange(newData, {
        year: removedYear,
        action: 'delete',
      });
    }
  };

  return (
    <div className="data-table-container">
      {title && (
        <div className="data-table-header">
          <h3 className="data-table-title">{title}</h3>
          {subtitle ? <p className="data-table-subtitle">{subtitle}</p> : null}
        </div>
      )}
      
      <div className="data-table-controls">
        <div className="data-table-search">
          <input
            type="text"
            placeholder="Search data (e.g., 2020, AADT, Replica, EMFAC)..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(0); // Reset to first page when searching
            }}
            className="data-table-search-input"
          />
        </div>
        
        <button 
          onClick={addNewRow}
          className="data-table-add-button"
        >
          + Add Row
        </button>
      </div>

      <div className="data-table-wrapper" style={{ maxHeight }}>
        <table className="data-table">
          <thead>
            <tr>
              {columns.map(column => (
                <th key={column.key} className="data-table-header">
                  {column.label}
                </th>
              ))}
              <th className="data-table-header data-table-actions-header">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, rowIndex) => {
              const rowKey = highlightKey ? row[highlightKey] : undefined;
              const isHighlighted =
                highlightedRows && rowKey !== undefined && highlightedRows.has(rowKey);
              const isSecondaryHighlighted =
                secondaryHighlightedRows &&
                rowKey !== undefined &&
                secondaryHighlightedRows.has(rowKey);
              
              return (
                <tr 
                  key={rowIndex} 
                  className={`data-table-row${isHighlighted ? ' data-table-row-highlighted' : ''}${isSecondaryHighlighted ? ' data-table-row-scaled' : ''}`}
                >
                {columns.map(column => {
                  const cellKey = `${startIndex + rowIndex}-${column.key}`;
                  const hasError = errors[cellKey];
                  const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.colKey === column.key;
                  
                  return (
                    <td 
                      key={column.key} 
                      className={`data-table-cell ${hasError ? 'data-table-cell-error' : ''} ${column.editable !== false ? 'data-table-cell-editable' : ''}`}
                      onClick={() => handleCellClick(rowIndex, column.key)}
                    >
                      {isEditing ? (
                        <input
                          type={column.type === 'number' ? 'number' : 'text'}
                          value={row[column.key] || ''}
                          onChange={(e) => handleCellEdit(rowIndex, column.key, e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === 'Escape') {
                              handleCellBlur();
                            }
                          }}
                          className="data-table-input"
                          autoFocus
                        />
                      ) : (
                        <span>{formatCellValue(row[column.key], column)}</span>
                      )}
                      {hasError && (
                        <div className="data-table-error-message">{hasError}</div>
                      )}
                    </td>
                  );
                })}
                <td className="data-table-cell data-table-actions">
                  <button 
                    onClick={() => deleteRow(rowIndex)}
                    className="data-table-delete-button"
                    title="Delete row"
                  >
                    🗑️
                  </button>
                </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="data-table-pagination">
          <button 
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            className="data-table-pagination-button"
          >
            Previous
          </button>
          
          <span className="data-table-pagination-info">
            Page {currentPage + 1} of {totalPages} ({filteredData.length} rows)
          </span>
          
          <button 
            onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
            disabled={currentPage === totalPages - 1}
            className="data-table-pagination-button"
          >
            Next
          </button>
        </div>
      )}

      {onRestoreDefaults && (
        <div className="data-table-footer">
          <button 
            onClick={onRestoreDefaults}
            className="data-table-restore-button"
            title="Reset all data to original values from database"
          >
            ↺ Restore Defaults
          </button>
        </div>
      )}
    </div>
  );
};
